import React, { useRef, FC, DragEvent, ChangeEvent, useState, forwardRef, useImperativeHandle } from "react";
import { infoIcon, uploadIcon } from "utils/constant";

interface FileUploadProps {
  label?: string;
  fileTypes?: string; // comma-separated types like "image/*,.pdf"
  required?: boolean;
  info?: string;
  maxSizeMB?: number;
  previewImage?: boolean; // optional image preview
  onChange: (file: File | null) => void;
  error?: string; // external error prop for form validation
}

const FileUpload = forwardRef<HTMLInputElement, FileUploadProps>(({
  label = "Attachment",
  fileTypes = "*",
  required = false,
  info = "",
  maxSizeMB,
  previewImage = false,
  onChange,
  error: externalError,
}, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => inputRef.current!);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [internalError, setInternalError] = useState("");

  // Use external error if provided, otherwise use internal error
  const displayError = externalError || internalError;

  const handleFile = (file: File | null) => {
    if (!file) return;

    // Validate file type
    if (fileTypes !== "*" && !fileTypes.split(",").some((type) => file.type.match(type.trim()))) {
      setInternalError(`Invalid file type. Supported types: ${fileTypes}`);
      setSelectedFile(null);
      onChange(null);
      return;
    }

    // Validate file size
    if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
      setInternalError(`File exceeds the maximum size of ${maxSizeMB}MB`);
      setSelectedFile(null);
      onChange(null);
      return;
    }

    setInternalError("");
    setSelectedFile(file);
    onChange(file);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    handleFile(file);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    handleFile(file);
  };

  const clearFile = () => {
    setSelectedFile(null);
    setInternalError("");
    onChange(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="flex flex-col w-full">
      {/* Label */}
      <label
        htmlFor="file-upload"
        className="flex items-center gap-2 text-gray-700 font-medium mb-2"
      >
        {infoIcon}
        {label}
        {required && (
          <span className="text-red-500" title="This field is required">
            *
          </span>
        )}
      </label>

      {/* Selected File */}
      {selectedFile ? (
        <div className="flex flex-col items-start rounded-lg border border-gray-500 bg-gray-50 p-4 shadow-sm w-full hover:shadow-md transition-all">
          {previewImage && selectedFile.type.startsWith("image/") && (
            <img
              src={URL.createObjectURL(selectedFile)}
              alt={selectedFile.name}
              className="w-24 h-24 object-cover rounded mb-2"
            />
          )}
          <span
            className="break-words max-w-full font-medium text-gray-800"
            title={selectedFile.name}
          >
            {selectedFile.name}
          </span>
          <span className="text-xs text-gray-600 mt-1">
            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
          </span>
          <button
            type="button"
            onClick={clearFile}
            className="text-red-400 text-sm mt-1 hover:underline"
          >
            Remove file
          </button>
        </div>
      ) : (
        // Dropzone
        <div
          className={`group relative flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-12 transition 
            ${isDragOver ? "border-gray-300 bg-gray-50 animate-pulse" : "border-gray-30 bg-gray-50"} 
            hover:border-gold-500 hover:bg-gold-50 hover:shadow-md
          `}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          aria-label="File upload dropzone"
        >
          {uploadIcon}
          <p className="text-gray-500 group-hover:text-gray-700 text-center mt-2">
            Drag & drop a file here or click to upload
          </p>
          {fileTypes !== "*" && (
            <p className="text-xs text-gray-400 mt-1">
              Supported types: {fileTypes}
            </p>
          )}
          {/* {maxSizeMB && (
            <p className="text-xs text-gray-400 mt-1">
              Max size: {maxSizeMB} MB
            </p>
          )} */}
        </div>
      )}

      {/* Hidden input */}
      <input
        id="file-upload"
        type="file"
        accept={fileTypes}
        ref={inputRef}
        className="hidden"
        onChange={handleChange}
      />

      {/* Info and Error */}
      {info && <p className="mt-1 text-xs text-gray-500">{info}</p>}
      {displayError && <p className="mt-1 text-xs text-red-500">{displayError}</p>}
    </div>
  );
});

FileUpload.displayName = 'FileUpload';

export default FileUpload;

